import { useState, useEffect } from 'react'
import { X, Search, ShieldCheck } from 'lucide-react'
import api from '../core/services/api'

interface UserOption {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: string
  module_permissions: Record<string, string[]> | null
}

interface RequestApprovalModalProps {
  isOpen: boolean
  onClose: () => void
  entityType: 'offerta' | 'commessa'
  entityId: string
  onSubmitted: () => void
}

// Ruoli che hanno approve di default
const ROLES_WITH_APPROVE = ['admin', 'manager']

export function RequestApprovalModal({
  isOpen,
  onClose,
  entityType,
  entityId,
  onSubmitted,
}: RequestApprovalModalProps) {
  const [users, setUsers] = useState<UserOption[]>([])
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) {
      loadApprovers()
      setSearch('')
      setSelectedId(null)
      setNote('')
      setError('')
    }
  }, [isOpen])

  const loadApprovers = async () => {
    try {
      setLoading(true)
      const res = await api.get('/v1/anagrafiche/utenti')
      // Filtra utenti che possono approvare
      const module = entityType === 'offerta' ? 'offerte' : 'commesse'
      const approvers = (res.data as UserOption[]).filter(u => {
        // Admin ha sempre tutti i permessi
        if (u.role === 'admin') return true
        // Se ha permessi custom, controlla il modulo
        if (u.module_permissions && u.module_permissions[module]) {
          return u.module_permissions[module].includes('approve')
        }
        // Altrimenti usa il default del ruolo
        return ROLES_WITH_APPROVE.includes(u.role)
      })
      setUsers(approvers)
    } catch {
      setError('Errore nel caricamento utenti')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!selectedId) return
    try {
      setSaving(true)
      setError('')
      const endpoint = entityType === 'offerta'
        ? `/v1/workflow/offerte/${entityId}/richiedi-approvazione`
        : `/v1/workflow/commesse/${entityId}/richiedi-approvazione`
      await api.post(endpoint, {
        approvatore_id: selectedId,
        note: note || null,
      })
      onSubmitted()
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Errore nella richiesta')
    } finally {
      setSaving(false)
    }
  }

  const filtered = users.filter(u => {
    const q = search.toLowerCase()
    return u.email.toLowerCase().includes(q) ||
      (u.first_name?.toLowerCase() || '').includes(q) ||
      (u.last_name?.toLowerCase() || '').includes(q)
  })

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Richiedi Approvazione</h2>
            <p className="text-sm text-gray-500">Seleziona chi deve approvare</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca approvatore..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            />
          </div>

          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
            {loading ? (
              <div className="p-4 text-center text-gray-500 text-sm">Caricamento...</div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">Nessun approvatore disponibile</div>
            ) : (
              filtered.map(u => (
                <button
                  key={u.id}
                  onClick={() => setSelectedId(u.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                    selectedId === u.id ? 'bg-purple-50 border-l-2 border-purple-500' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900 text-sm">
                        {u.first_name} {u.last_name}
                      </div>
                      <div className="text-xs text-gray-500">{u.email}</div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {u.role === 'admin' ? 'Admin' : u.role === 'manager' ? 'Manager' : u.role}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note (opzionale)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Aggiungi una nota per l'approvatore..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
          >
            Annulla
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedId || saving}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
          >
            <ShieldCheck className="w-4 h-4" />
            {saving ? 'Invio...' : 'Richiedi Approvazione'}
          </button>
        </div>
      </div>
    </div>
  )
}
